import { type FC } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';
import dibsCss from 'dibs-css';
import { Link } from 'dibs-elements/exports/Link.tsx';

import Photo from 'dibs-icons/exports/Photo.ts';
import { SharedContactDealerWrapper } from '../../../contactDealer/SharedContactDealerWrapper.tsx';

import { type HeroCarouselRequestMore_user$key } from './__generated__/HeroCarouselRequestMore_user.graphql.ts';
import { type HeroCarouselRequestMore_item$key } from './__generated__/HeroCarouselRequestMore_item.graphql.ts';

import styles from './HeroCarouselRequestMore.scss';

const itemFragment = graphql`
    fragment HeroCarouselRequestMore_item on Item {
        ...SharedContactDealerWrapper_item
    }
`;

const userFragment = graphql`
    fragment HeroCarouselRequestMore_user on User {
        ...SharedContactDealerWrapper_user
    }
`;

const ContactSellerButton: FC<{ onClick?: () => void }> = ({ onClick = () => {} }) => (
    <Link
        buyerLinkType="contrastLinkNoEffect"
        className={classnames(
            dibsCss.flex,
            dibsCss.itemsCenter,
            dibsCss.justifyCenter,
            dibsCss.roundedFull,
            dibsCss.cursorPointer,
            styles.button
        )}
        onClick={onClick}
    >
        <FormattedMessage
            id="HeroCarouselRequestMore.contactSeller"
            defaultMessage="Contact Seller"
        />
    </Link>
);

export const HeroCarouselRequestMore: FC<{
    user: HeroCarouselRequestMore_user$key | null | undefined;
    item: HeroCarouselRequestMore_item$key;
    disableRequestMoreImagesModal?: boolean;
    currency: string;
}> = ({ user: userRef, item: itemRef, disableRequestMoreImagesModal = false, currency }) => {
    const user = useFragment(userFragment, userRef);
    const item = useFragment(itemFragment, itemRef);

    return (
        <div
            className={classnames(
                dibsCss.flex,
                dibsCss.itemsCenter,
                dibsCss.justifyCenter,
                dibsCss.wFull,
                dibsCss.hFull
            )}
        >
            <div
                className={classnames(
                    dibsCss.flex,
                    dibsCss.flexCol,
                    dibsCss.itemsCenter,
                    dibsCss.gapSmall,
                    dibsCss.maxW400px,
                    dibsCss.textCenter,
                    dibsCss.textNoir
                )}
            >
                <Photo variation="48" />
                <div className={classnames(dibsCss.flex, dibsCss.flexCol, dibsCss.gapXsmall)}>
                    <div className={dibsCss.sassyFontHeader}>
                        <FormattedMessage
                            id="HeroCarouselRequestMore.header"
                            defaultMessage="Want more images or videos?"
                        />
                    </div>
                    <div className={dibsCss.sassyFontBody}>
                        <FormattedMessage
                            id="HeroCarouselRequestMore.subheader"
                            defaultMessage="Request additional images or videos from the seller."
                        />
                    </div>
                </div>
                {disableRequestMoreImagesModal ? (
                    <ContactSellerButton />
                ) : (
                    <SharedContactDealerWrapper
                        item={item}
                        user={user}
                        launchOrigin="pdp image carousel"
                        modalType="REQUEST_IMAGES"
                        currency={currency}
                    >
                        {(onClick: $TSFixMe) => <ContactSellerButton onClick={onClick} />}
                    </SharedContactDealerWrapper>
                )}
            </div>
        </div>
    );
};
